import React, { useEffect, useState, useRef } from "react";
import "../Integrations/Dashboard.css";
import { OogwaiLogo2, OogwaiTextLogo } from "../../components/Icons/iconImages";
import Loading from "../../components/Loading";
import IconRings from "../../components/IconRings";
import { Controller, useForm } from "react-hook-form";
import { Button } from "../../components/Buttons";
import InputField from "../../components/InputField";
import { Input } from 'antd';
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateProfileFirstTimeMutation, useUpdateProfileMutation, useUpdateWorkspaceMutation } from "../../services/createProfile";
import { useDispatch } from "react-redux";
import { baseApi } from "../../services/baseQuery";
import { workSpaceId } from "../../services/staticSlice";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import ReactGA from "react-ga4";
import { toast } from "../../components/Toast/toast";
import useIsInIframe from "../../Hooks/useIsInIframe";
import AskAILogo from "../../components/Icons/AskAILogo";

const { TextArea } = Input;

const CreateProfileFirst = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const oldProfile = location.pathname.split("/")[2];
    const hasNavigated = useRef(false);
    const isInIframe = useIsInIframe();


    // Get workspace context for better integration
    const { refreshWorkspaces, switchWorkspace } = useWorkspace();

    const [createProfileFirst, {
        data: createProfileData,
        error: createProfileError,
        isLoading: isCreatingProfile,
        isSuccess: isCreateProfileSuccess,
        isError: isCreateProfileError
    }] = useCreateProfileFirstTimeMutation();

    const [updateProfile, {
        data: updateProfileData,
        error: updateProfileError,
        isLoading: isUpdatingProfile,
        isSuccess: isUpdateProfileSuccess,
        isError: isUpdateProfileError
    }] = useUpdateProfileMutation();

    const [updateWorkSpace, {
        isLoading: isUpdatingWorkspace,
        isError: isUpdateWorkspaceError,
        error: updateWorkspaceError
    }] = useUpdateWorkspaceMutation();

    const defaultValues = {
        workspace_id: '',
        profile_description: '',
    };
    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue
    } = useForm({
        defaultValues,
    });

    // Set form values from location state
    useEffect(() => {
        if (location?.state?.data) {
            setValue('workspace_id', location?.state?.data?.workspace_id);
            setValue('profile_description', location?.state?.data?.profile_description);
        }
    }, [location?.state?.data, setValue]);

    // Monitor all state changes
    useEffect(() => {
        console.log('State changed - isCreatingProfile:', isCreatingProfile);
    }, [isCreatingProfile]);

    useEffect(() => {
        console.log('State changed - isCreateProfileSuccess:', isCreateProfileSuccess);
    }, [isCreateProfileSuccess]);

    useEffect(() => {
        console.log('State changed - isCreateProfileError:', isCreateProfileError);
    }, [isCreateProfileError]);

    useEffect(() => {
        console.log('State changed - createProfileData:', createProfileData);
    }, [createProfileData]);

    useEffect(() => {
        console.log('State changed - createProfileError:', createProfileError);
    }, [createProfileError]);

    // Handle create profile response
    useEffect(() => {
        console.log('useEffect triggered:', {
            isCreateProfileSuccess,
            createProfileData,
            isCreatingProfile,
            isCreateProfileError,
            createProfileError,
            hasNavigated: hasNavigated.current
        });

        if (isCreateProfileSuccess && createProfileData && !hasNavigated.current) {
            hasNavigated.current = true;
            const workspaceId = createProfileData.workspace_id;
            const workspaceName = createProfileData.workspace_name;
            console.log('Profile created successfully, workspace_id (MongoDB ID):', workspaceId);
            console.log('Workspace name:', workspaceName);
            console.log('Full response data:', createProfileData);
            console.log('navigate function:', navigate);
            console.log('Current location:', window.location.pathname);

            if (!workspaceId) {
                console.error('No workspace_id in response!');
                toast.error('Profile created but workspace_id missing');
                return;
            }

            ReactGA.event('workspace_created', {
                method: 'manual',
                user_type: 'new_user',
                workspace_name: workspaceName || 'unknown',
            });

            toast.success(createProfileData.message || 'Profile created successfully');

            // Set the workspace_id (MongoDB ID) immediately to prevent HeaderNav redirect
            console.log('Setting workspace_id (MongoDB ID) in localStorage:', workspaceId);
            localStorage.setItem('workspace_id', workspaceId);

            // Also update Redux state immediately
            dispatch(workSpaceId(workspaceId));

            // Navigate with workspace MongoDB ID in URL
            console.log('About to navigate to /integrations with workspace MongoDB ID');
            navigate(`/integrations?workspace=${workspaceId}`);

            // Refresh workspaces in background
            console.log('Refreshing workspaces');
            refreshWorkspaces();

            // Reset API state after navigation
            setTimeout(() => {
                console.log('Resetting API state');
                dispatch(baseApi.util.resetApiState());
            }, 2000);
        }
    }, [isCreateProfileSuccess, createProfileData]);

    // Handle create profile error
    useEffect(() => {
        if (isCreateProfileError && createProfileError) {
            const errorMessage = (createProfileError as any)?.data?.error || 'Failed to create profile';
            toast.error(errorMessage);
        }
    }, [isCreateProfileError, createProfileError]);

    // Handle update profile response
    useEffect(() => {
        if (isUpdateProfileSuccess && updateProfileData) {
            toast.success(updateProfileData.message || 'Profile updated successfully');
            navigate('/integrations', {
                state: {
                    activeTab: '2'
                }
            });
        }
    }, [isUpdateProfileSuccess, updateProfileData, navigate]);

    // Handle update profile error
    useEffect(() => {
        if (isUpdateProfileError && updateProfileError) {
            const errorMessage = (updateProfileError as any)?.data?.error || 'Failed to update profile';
            toast.error(errorMessage);
        }
    }, [isUpdateProfileError, updateProfileError]);

    // Handle update workspace error
    useEffect(() => {
        if (isUpdateWorkspaceError && updateWorkspaceError) {
            console.error('Failed to update workspace:', updateWorkspaceError);
            console.error('Full error object:', JSON.stringify(updateWorkspaceError, null, 2));
        }
    }, [isUpdateWorkspaceError, updateWorkspaceError]);

    const handleUpdateProfile = (data: any) => {
        const reqmodel = { ...data, oldProfile };
        updateProfile(reqmodel);
    };


    const handleCreateProfile = (data: any) => {
        console.log('handleCreateProfile called with data:', data);
        console.log('About to call createProfileFirst');
        createProfileFirst(data);
    };

    const isLoading = isCreatingProfile || isUpdatingProfile;

    if (isLoading) {
        return <Loading />;
    }
    return (
        <>
            <section className="relative px-4 sm:px-6 md:px-8" >
                <div className="h-5 overflow-visible hidden sm:block">
                    <IconRings ringColor={'#b6f0d5ab'} numRings={10} ringBaseSize={26} ringSpacing={80} className="mt-32" />
                </div>
                <div className="relative flex flex-col h-full justify-between rounded-2xl sm:rounded-3xl bg-white max-w-[576px] mx-auto border border-gray-200 shadow-sm my-4 sm:my-0" >
                    <div className="px-6 sm:px-8 md:px-12 pt-4 sm:pt-6 pb-4">
                        <div className="flex flex-col pb-4 items-center">
                            {isInIframe ? <AskAILogo className="w-36 h-36 sm:w-[72px] sm:h-[72px]" /> : <OogwaiLogo2 className="w-16 h-16 sm:w-[72px] sm:h-[72px]" />}
                            {isInIframe ? <h3>Ask AI</h3> : <OogwaiTextLogo className="h-6 sm:h-auto" />}
                        </div>
                        <div className="flex flex-col gap-2 items-center">
                            <h3 className="text-[#414651] font-semibold text-lg sm:text-xl">{oldProfile ? "Edit Workspace" : "Create Workspace"}</h3>
                            <p className="text-xs sm:text-sm text-center w-full sm:w-[80%] md:w-[60%] px-2">Workspaces are a unique combination of data inputs you select when connection your external analytics accounts.</p>
                        </div>


                    </div>
                    <form onSubmit={(e) => {
                        console.log('Form submitted');
                        console.log('oldProfile:', oldProfile);
                        console.log('Will call:', oldProfile ? 'handleUpdateProfile' : 'handleCreateProfile');
                        handleSubmit(oldProfile ? handleUpdateProfile : handleCreateProfile)(e);
                    }} className="space-y-4 sm:space-y-5">
                        <div className="px-6 sm:px-8 md:px-12 pt-4 pb-4">
                            <InputField
                                control={control}
                                label={'Workspace Name'}
                                placeholder={'Workspace Name'}
                                type='text'
                                name="workspace_id"
                                rules={{
                                    required: 'Workspace Name is required',
                                }}
                                error={errors.workspace_id}
                                hint="e.g., {brandname}-workspace"
                            />

                            {/* <Controller
                                name={'profile_description'}
                                control={control}
                                render={({ field, fieldState: { error: fieldError } }) => (
                                    <>
                                        <label htmlFor={"input"} className="text-sm font-medium text-[#414651]">
                                            {'Description'}
                                        </label>
                                        <TextArea
                                            onChange={(e) => {
                                                field.onChange(e);
                                            }}
                                            value={field.value}
                                            rows={4}
                                            placeholder="Profile description"
                                            className="rounded-2xl"
                                        />
                                    </>
                                )}
                            /> */}
                        </div>
                        <hr />

                        <div className="pb-4 px-4 sm:pr-4">
                            <Button
                                type='primary'
                                htmlType="submit"
                                className="w-full sm:w-32 flex sm:ml-auto items-center justify-center"
                                disabled={isLoading}
                                loading={isLoading}
                            >
                                {oldProfile ? "Update Workspace" : "Create Workspace"}
                            </Button>
                        </div>
                    </form>

                </div>
            </section >

        </>
    );
};

export default CreateProfileFirst;